import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import employeeDeploymentService from '../../services/employeeDeploymentService';
import employeeService from '../../services/employeeService';
import clientSiteService from '../../services/clientSiteService';
import NotificationToast from '../../components/ui/NotificationToast';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import AddDeploymentModal from './components/AddDeploymentModal';
import TransferDeploymentModal from './components/TransferDeploymentModal';

const EmployeeDeploymentManagement = () => {
  const { userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const [searchParams] = useSearchParams();
  const employeeIdFilter = searchParams.get('employee_id');
  const clientSiteIdFilter = searchParams.get('client_site_id');
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deployments, setDeployments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clientSites, setClientSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEmployee, setFilterEmployee] = useState(employeeIdFilter || 'all');
  const [filterSite, setFilterSite] = useState(clientSiteIdFilter || 'all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [editingDeployment, setEditingDeployment] = useState(null);
  const [transferringDeployment, setTransferringDeployment] = useState(null);
  const [notification, setNotification] = useState(null);

  const user = {
    name: userProfile?.full_name || "Admin",
    email: userProfile?.email || "admin@facilitypro.com",
    role: userProfile?.role || "Admin"
  };

  useEffect(() => {
    loadEmployees();
    loadClientSites();
    loadDeployments();
  }, [searchTerm, filterEmployee, filterSite, filterStatus]);

  const loadEmployees = async () => {
    try {
      const result = await employeeService.getEmployees({ status: 'active' });
      if (result?.success) {
        setEmployees(result.data || []);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadClientSites = async () => {
    try {
      const result = await clientSiteService.getClientSites({ is_active: 'true' });
      if (result?.success) {
        setClientSites(result.data || []);
      }
    } catch (error) {
      console.error('Error loading client sites:', error);
    }
  };

  const loadDeployments = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (filterEmployee !== 'all') filters.employee_id = filterEmployee;
      if (filterSite !== 'all') filters.client_site_id = filterSite;
      if (filterStatus !== 'all') filters.status = filterStatus;
      
      const result = await employeeDeploymentService.getDeployments(filters);
      if (result?.success) {
        setDeployments(result.data || []);
      } else {
        setNotification({ message: result?.error || 'Failed to load deployments', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading deployments:', error);
      setNotification({ message: 'An error occurred while loading deployments', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeployment = () => {
    setEditingDeployment(null);
    setShowAddModal(true);
  };

  const handleEditDeployment = (deployment) => {
    setEditingDeployment(deployment);
    setShowAddModal(true);
  };

  const handleTransferDeployment = (deployment) => {
    setTransferringDeployment(deployment);
    setShowTransferModal(true);
  };

  const handleDeleteDeployment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deployment record?')) return;
    
    try {
      const result = await employeeDeploymentService.deleteDeployment(id);
      if (result?.success) {
        setNotification({ message: 'Deployment deleted successfully', type: 'success' });
        loadDeployments();
      } else {
        setNotification({ message: result?.error || 'Failed to delete deployment', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleDeploymentSaved = () => {
    setShowAddModal(false);
    setEditingDeployment(null);
    loadDeployments();
  };

  const handleTransferComplete = () => {
    setShowTransferModal(false);
    setTransferringDeployment(null);
    loadDeployments();
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.full_name} (${employee.employee_id})` : 'Unknown Employee';
  };

  const getSiteName = (siteId) => {
    const site = clientSites.find(s => s.id === siteId);
    return site ? `${site.site_name}` : 'Unknown Site';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'transferred':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        notifications={[]}
      />
      
      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          <BreadcrumbNavigation />
          
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Employee Deployment Management</h1>
              <p className="text-muted-foreground mt-2">
                Track employee deployments at client sites
              </p>
            </div>
            <Button onClick={handleAddDeployment} iconName="Plus" iconPosition="left">
              Add Deployment
            </Button>
          </div>

          <NotificationToast
            message={notification?.message}
            type={notification?.type}
            onDismiss={() => setNotification(null)}
          />

          {/* Filters */}
          <div className="mb-6 flex gap-4 items-center flex-wrap">
            <div className="flex-1 max-w-md">
              <Input
                placeholder="Search deployments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                iconName="Search"
              />
            </div>
            <select
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="all">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.full_name} ({emp.employee_id})</option>
              ))}
            </select>
            <select
              value={filterSite}
              onChange={(e) => setFilterSite(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="all">All Sites</option>
              {clientSites.map(site => (
                <option key={site.id} value={site.id}>{site.site_name}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="transferred">Transferred</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Deployments Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading deployments...</p>
            </div>
          ) : deployments.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-lg">
              <Icon name="Users" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
              <p className="text-muted-foreground">No deployments found</p>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm || filterEmployee !== 'all' || filterSite !== 'all' ? 'Try adjusting your filters' : 'Get started by adding your first deployment'}
              </p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-semibold text-foreground">Employee</th>
                      <th className="text-left p-4 font-semibold text-foreground">Deployed Site</th>
                      <th className="text-left p-4 font-semibold text-foreground">Client Site</th>
                      <th className="text-left p-4 font-semibold text-foreground">Start Date</th>
                      <th className="text-left p-4 font-semibold text-foreground">End Date</th>
                      <th className="text-left p-4 font-semibold text-foreground">Status</th>
                      <th className="text-left p-4 font-semibold text-foreground">Duration</th>
                      <th className="text-right p-4 font-semibold text-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deployments.map((deployment) => {
                      const startDate = new Date(deployment.deployment_start_date);
                      const endDate = deployment.deployment_end_date ? new Date(deployment.deployment_end_date) : null;
                      const today = new Date();
                      const duration = endDate 
                        ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
                        : Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
                      
                      return (
                        <tr key={deployment.id} className="border-t border-border hover:bg-muted/30">
                          <td className="p-4">
                            <div>
                              <div className="font-medium text-foreground">{deployment.full_name}</div>
                              <div className="text-sm text-muted-foreground">{deployment.employee_id}</div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="font-medium text-foreground">
                              {deployment.site_name || 'N/A'}
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <div className="font-medium text-foreground">{deployment.site_name}</div>
                              <div className="text-sm text-muted-foreground">{deployment.company_name}</div>
                            </div>
                          </td>
                          <td className="p-4 text-foreground">
                            {startDate.toLocaleDateString('en-IN')}
                          </td>
                          <td className="p-4 text-foreground">
                            {endDate ? endDate.toLocaleDateString('en-IN') : (
                              <span className="text-muted-foreground italic">Ongoing</span>
                            )}
                          </td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(deployment.status)}`}>
                              {deployment.status}
                            </span>
                          </td>
                          <td className="p-4 text-foreground">
                            {duration} day{duration !== 1 ? 's' : ''}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-2">
                              {deployment.status === 'active' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTransferDeployment(deployment)}
                                  title="Transfer"
                                >
                                  <Icon name="ArrowRight" size={16} />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditDeployment(deployment)}
                                title="Edit"
                              >
                                <Icon name="Edit" size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDeployment(deployment.id)}
                                className="text-destructive"
                                title="Delete"
                              >
                                <Icon name="Trash2" size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Deployment Modal */}
      <AddDeploymentModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingDeployment(null);
        }}
        deployment={editingDeployment}
        employees={employees}
        clientSites={clientSites}
        onSave={handleDeploymentSaved}
      />

      {/* Transfer Deployment Modal */}
      <TransferDeploymentModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setTransferringDeployment(null);
        }}
        deployment={transferringDeployment}
        clientSites={clientSites}
        onTransfer={handleTransferComplete}
      />
    </div>
  );
};

export default EmployeeDeploymentManagement;

